import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface Props {
  trigger: ReactNode;
  children: ReactNode;
  panelClassName?: string;
  triggerClassName?: string;
}

export default function HoverPopover({
  trigger,
  children,
  panelClassName = "",
  triggerClassName = "",
}: Props) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const id = useId();

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const recompute = () => {
      const t = triggerRef.current?.getBoundingClientRect();
      const p = panelRef.current?.getBoundingClientRect();
      if (!t || !p) return;
      const margin = 8;
      let top = t.bottom + 4;
      if (top + p.height > window.innerHeight - margin) {
        top = Math.max(margin, t.top - p.height - 4);
      }
      let left = t.right - p.width;
      left = Math.max(margin, Math.min(left, window.innerWidth - p.width - margin));
      setPos({ top, left });
    };
    recompute();
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
    };
  }, [open]);

  return (
    <span
      ref={triggerRef}
      className={`relative inline-flex align-middle ${triggerClassName}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClick={(e) => e.stopPropagation()}
      aria-describedby={open ? id : undefined}
    >
      {trigger}
      {open &&
        createPortal(
          <span
            id={id}
            ref={panelRef}
            role="tooltip"
            className={`pointer-events-none fixed z-50 ${panelClassName}`}
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? "visible" : "hidden",
            }}
          >
            {children}
          </span>,
          document.body,
        )}
    </span>
  );
}
